import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Loader2, Sparkles, Check, TriangleAlert, Info } from 'lucide-react';
import { labourService } from '../../../services/labourService';
import { PERIODS, periodLabel } from '../../../utils/financialPeriods';

// =============================================================================
// Variable Labour > OPTIMISE THE YEAR
//
// The page-level version of the organiser's per-month optimiser: set the rules
// once, size every month against its own volume, review the year as a table,
// apply the ones you accept.
//
// WHY WHOLE-YEAR IS THE DEFAULT AND NOT A CONVENIENCE. A roster is not scaled
// month to month — it is re-decided. February holds exactly four of every
// weekday; March holds five Mondays and five Sundays. At double time that is a
// different answer, not the same answer times 1.1, which is why each period is
// solved on its own calendar rather than one pattern being stretched across
// twelve.
//
// THE RULES BOX IS THE POINT. Every constraint here NARROWS the search: it can
// only make the answer worse on paper and better in the plant. A solver that
// cannot be told "we do not run twelves" and "we have three crews" produces
// rosters nobody can staff, and gets ignored.
//
// NOTHING IS SAVED HERE. Accepted months are written into the page's own
// `months` state and marked dirty; the page's existing Save button commits
// them and the real calc then produces the authoritative hours. No new write
// path — the same ruling as the network optimiser and ai/operations.js.
// =============================================================================

const HOUR_CHOICES = [6, 8, 12];
const fmtH = (n) => `${Math.round(Number(n || 0)).toLocaleString()}h`;
const num = (n, d = 0) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: d });

const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors';
const btnPrimary = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-colors';

const Seg = ({ value, onChange, options, disabled }) => (
  <div className="inline-flex rounded-md border border-slate-300 dark:border-slate-600 overflow-hidden">
    {options.map(([v, label]) => (
      <button key={v} type="button" disabled={disabled} onClick={() => onChange(v)}
        className={`px-2.5 py-1 text-[11px] font-medium transition-colors disabled:opacity-40 ${value === v
          ? 'bg-violet-600 text-white'
          : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
        {label}
      </button>
    ))}
  </div>
);

const Field = ({ label, hint, children }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</span>
    {children}
    {hint && <span className="text-[10px] text-slate-400 dark:text-slate-500">{hint}</span>}
  </label>
);

export default function OptimiseScheduleModal({
  moduleCode, functionCode, functionName, fyStartMonth, onApply, onClose,
}) {
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [plan, setPlan] = useState(null);
  const [accepted, setAccepted] = useState(() => new Set());

  // ---- what drives the hours ----------------------------------------------
  const [source, setSource] = useState('production');
  const [basis, setBasis] = useState('pallets');
  const [workedRate, setWorkedRate] = useState('');
  const [paidRate, setPaidRate] = useState('');

  // ---- the rules (mirrors the Shift setup strip on the page) --------------
  const [hourOptions, setHourOptions] = useState([...HOUR_CHOICES]);
  const [dayStartHour, setDayStartHour] = useState(0);
  const [assignMode, setAssignMode] = useState('timeslot');
  const [maxCrews, setMaxCrews] = useState(0);            // 0 = every crew
  const [alternating, setAlternating] = useState(false);
  const [rotationWeeks, setRotationWeeks] = useState(2);

  useEffect(() => {
    let dead = false;
    (async () => {
      setLoading(true); setError('');
      try {
        const c = await labourService.getOptimiseContext(moduleCode, functionCode);
        if (dead) return;
        setCtx(c);
        setSource(c.defaultSource || 'production');
        setMaxCrews(c.crews?.length || 0);
        // Seed the target with the rate the plan already runs at, averaged over
        // the periods that have one — a planner who cannot name their standard
        // cold can almost always say whether last year's number was good.
        const rates = (c.implied || [])
          .map((x) => x?.[c.defaultSource || 'production']?.perPallet)
          .filter((v) => v > 0);
        if (rates.length) {
          setWorkedRate(String(Math.round((rates.reduce((a, b) => a + b, 0) / rates.length) * 10000) / 10000));
        }
      } catch (e) {
        if (!dead) setError(e.response?.data?.message || e.response?.data?.error || e.message);
      } finally { if (!dead) setLoading(false); }
    })();
    return () => { dead = true; };
  }, [moduleCode, functionCode]);

  const toggleHour = (h) => setHourOptions((prev) => {
    const next = prev.includes(h) ? prev.filter((x) => x !== h) : [...prev, h].sort((a, b) => a - b);
    // Never let the list empty out — "no shift length is allowed" is not a
    // constraint, it is an unanswerable question.
    return next.length ? next : prev;
  });

  const run = useCallback(async () => {
    setBusy(true); setError(''); setPlan(null); setAccepted(new Set());
    try {
      const r = await labourService.optimise(moduleCode, functionCode, {
        periods: PERIODS,
        source,
        basis,
        worked_hours_per_unit: Number(workedRate) || undefined,
        paid_hours_per_unit: Number(paidRate) || undefined,
        hour_options: hourOptions,
        max_crews: maxCrews || undefined,
      });
      if (!r.success) setError(r.message || 'No roster could be proposed.');
      else {
        setPlan(r);
        // Pre-tick every month that solved. Unlike the network optimiser this
        // is a same-shape replacement of a schedule the planner is already
        // editing, not a write to another team's data — and twelve manual ticks
        // to accept an answer you asked for is friction, not safety. The Apply
        // button still names the count and nothing reaches the database until
        // the page's own Save.
        setAccepted(new Set(r.results.filter((x) => x.ok).map((x) => x.financial_period)));
      }
    } catch (e) {
      setError(e.response?.data?.message || e.response?.data?.error || e.message);
    } finally { setBusy(false); }
  }, [moduleCode, functionCode, source, basis, workedRate, paidRate, hourOptions, maxCrews]);

  const rows = plan?.results || [];
  const solved = rows.filter((r) => r.ok);
  const acceptedRows = solved.filter((r) => accepted.has(r.financial_period));

  const totals = useMemo(() => ({
    worked: acceptedRows.reduce((a, r) => a + r.best.workedHours, 0),
    paid: acceptedRows.reduce((a, r) => a + r.best.paidHours, 0),
    target: acceptedRows.reduce((a, r) => a + (r.target.workedHours || 0), 0),
  }), [acceptedRows]);

  const toggle = (p) => setAccepted((prev) => {
    const next = new Set(prev);
    if (next.has(p)) next.delete(p); else next.add(p);
    return next;
  });

  const apply = () => {
    onApply(acceptedRows.map((r) => ({
      periodIdx: r.financial_period - 1,
      pattern: r.best,
      rules: { dayStartHour, assignMode, alternating, rotationWeeks },
    })));
    onClose();
  };

  const unitWord = basis === 'pallets' ? 'pallet' : 'unit';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-6" onClick={onClose}>
      <div className="w-full max-w-5xl max-h-[90vh] flex flex-col rounded-xl bg-white dark:bg-slate-800 shadow-2xl overflow-hidden"
           onClick={(e) => e.stopPropagation()}>

        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h2 className="m-0 text-base font-bold text-slate-900 dark:text-slate-100">
              Optimise the year &middot; {functionName || functionCode}
            </h2>
            <p className="m-0 mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
              Every month is sized against its own volume and its own calendar. Nothing is saved until you Apply and then Save.
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="flex items-center gap-2 px-5 py-6 text-xs text-slate-500 dark:text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Reading the volume plan&hellip;
            </div>
          )}

          {ctx && (
            <>
              {/* ---- 1 · what drives the hours ---- */}
              <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700">
                <p className="m-0 mb-2.5 text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  1 &middot; What drives the hours
                </p>
                <div className="flex flex-wrap items-end gap-4">
                  <Field label="Volume driver" hint={source === 'production' ? 'What the plant makes' : 'What leaves the site'}>
                    <Seg value={source} onChange={(v) => { setSource(v); setPlan(null); }}
                      options={[['production', 'Production'], ['dispatch', 'Dispatch']]} />
                  </Field>
                  <Field label="Measured in">
                    <Seg value={basis} onChange={(v) => { setBasis(v); setPlan(null); }}
                      options={[['pallets', 'Pallets'], ['units', 'Units']]} />
                  </Field>
                  <Field label={`Worked hours / ${unitWord}`} hint="Your standard">
                    <input type="number" step="0.0001" min={0} value={workedRate}
                      onChange={(e) => { setWorkedRate(e.target.value); setPlan(null); }}
                      className="w-28 px-2 py-1.5 text-xs rounded-md border border-violet-300 dark:border-violet-500/40 bg-white dark:bg-slate-900 font-mono" />
                  </Field>
                  <Field label={`Paid hours / ${unitWord}`} hint="Optional">
                    <input type="number" step="0.0001" min={0} value={paidRate}
                      onChange={(e) => { setPaidRate(e.target.value); setPlan(null); }}
                      className="w-28 px-2 py-1.5 text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 font-mono" />
                  </Field>
                </div>
              </div>

              {/* ---- 2 · the rules ---- */}
              <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700">
                <p className="m-0 mb-2.5 text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  2 &middot; Rules the roster must obey
                </p>
                <div className="flex flex-wrap items-end gap-5">
                  <Field label="Shift lengths allowed" hint="Untick what you never run">
                    <div className="inline-flex rounded-md border border-slate-300 dark:border-slate-600 overflow-hidden">
                      {HOUR_CHOICES.map((h) => (
                        <button key={h} type="button" onClick={() => { toggleHour(h); setPlan(null); }}
                          className={`px-2.5 py-1 text-[11px] font-medium transition-colors ${hourOptions.includes(h)
                            ? 'bg-violet-600 text-white'
                            : 'bg-white dark:bg-slate-800 text-slate-400 dark:text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
                          {h}h
                        </button>
                      ))}
                    </div>
                  </Field>

                  <Field label="Day starts" hint="Blocks are cut from here">
                    <select value={dayStartHour} onChange={(e) => setDayStartHour(Number(e.target.value))}
                      className="px-2 py-1.5 text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 font-mono">
                      {Array.from({ length: 24 }, (_, h) => (
                        <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Crews to use" hint={`${ctx.crews?.length || 0} available`}>
                    <input type="number" min={1} max={ctx.crews?.length || 1} value={maxCrews}
                      onChange={(e) => { setMaxCrews(Number(e.target.value) || 0); setPlan(null); }}
                      className="w-20 px-2 py-1.5 text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 font-mono" />
                  </Field>

                  <Field label="Assign" hint={assignMode === 'perday' ? 'Template painted onto real dates' : 'Repeating weekly template'}>
                    <Seg value={assignMode} onChange={setAssignMode}
                      options={[['timeslot', 'By timeslot'], ['perday', 'Per day']]} />
                  </Field>

                  <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 cursor-pointer pb-1">
                    <input type="checkbox" checked={alternating} onChange={() => setAlternating((a) => !a)}
                      className="rounded border-slate-300 dark:border-slate-600" />
                    <span className="font-semibold text-violet-700 dark:text-violet-300">Alternating rotation</span>
                    <span className="text-[11px] text-slate-500 dark:text-slate-400">every</span>
                    <input type="number" min={1} value={rotationWeeks} disabled={!alternating}
                      onChange={(e) => setRotationWeeks(Math.max(1, Number(e.target.value) || 1))}
                      className="w-14 px-1.5 py-0.5 text-xs rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 font-mono disabled:opacity-40" />
                    <span className="text-[11px] text-slate-500 dark:text-slate-400">weeks</span>
                  </label>
                </div>

                <p className="m-0 mt-3 flex items-start gap-1.5 text-[11px] text-slate-400 dark:text-slate-500">
                  <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
                  Rotation and day-start do not change how many hours a roster works — they decide who works
                  which timeslot, and are carried onto every month you accept. Crew count and shift length do
                  change the answer, so tightening them will usually cost hours.
                </p>

                <div className="mt-3 flex items-center gap-3">
                  <button className={btnPrimary} onClick={run} disabled={busy || (!Number(workedRate) && !Number(paidRate))}>
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    {plan ? 'Run again' : 'Optimise all 12 months'}
                  </button>
                  {!Number(workedRate) && !Number(paidRate) && (
                    <span className="text-[11px] text-slate-400">Give a target rate first.</span>
                  )}
                </div>
              </div>
            </>
          )}

          {error && (
            <div className="mx-5 my-3 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10 flex gap-2">
              <TriangleAlert className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <p className="m-0 text-xs text-amber-800 dark:text-amber-300">{error}</p>
            </div>
          )}

          {/* ---- 3 · the year ---- */}
          {plan && (
            <div className="px-5 py-4">
              <div className="flex items-center justify-between mb-2">
                <p className="m-0 text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  3 &middot; The year &mdash; {solved.length} of {rows.length} months solved
                </p>
                <button className="text-[11px] text-violet-600 dark:text-violet-400 hover:underline"
                  onClick={() => setAccepted(accepted.size === solved.length
                    ? new Set() : new Set(solved.map((r) => r.financial_period)))}>
                  {accepted.size === solved.length ? 'Clear all' : 'Select all'}
                </button>
              </div>

              <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                <table className="w-full text-[11px]">
                  <thead className="bg-slate-50 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400">
                    <tr>
                      <th className="w-8 px-2 py-1.5" />
                      <th className="px-2 py-1.5 text-left font-medium">Month</th>
                      <th className="px-2 py-1.5 text-right font-medium">Volume</th>
                      <th className="px-2 py-1.5 text-right font-medium">Target</th>
                      <th className="px-2 py-1.5 text-right font-medium">Worked</th>
                      <th className="px-2 py-1.5 text-right font-medium">Paid</th>
                      <th className="px-2 py-1.5 text-right font-medium">Premium</th>
                      <th className="px-2 py-1.5 text-left font-medium">Roster</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const label = periodLabel(r.financial_period, fyStartMonth);
                      if (!r.ok) {
                        return (
                          <tr key={r.financial_period} className="border-t border-slate-100 dark:border-slate-700">
                            <td className="px-2 py-1.5" />
                            <td className="px-2 py-1.5 text-slate-400">{label}</td>
                            <td className="px-2 py-1.5 text-slate-400 italic" colSpan={6}>{r.reason}</td>
                          </tr>
                        );
                      }
                      const on = accepted.has(r.financial_period);
                      return (
                        <tr key={r.financial_period}
                            className={`border-t border-slate-100 dark:border-slate-700 ${on ? 'bg-violet-50/50 dark:bg-violet-500/5' : ''}`}>
                          <td className="px-2 py-1.5 text-center">
                            <input type="checkbox" checked={on} onChange={() => toggle(r.financial_period)}
                              className="rounded border-slate-300 dark:border-slate-600" />
                          </td>
                          <td className="px-2 py-1.5 text-slate-700 dark:text-slate-200 font-medium">
                            {label}
                            {r.unreachable && (
                              <span className="ml-1.5 text-amber-600 dark:text-amber-400" title="The crews cannot reach this target">short</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">{num(r.target.volume, 1)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">{r.target.workedHours != null ? fmtH(r.target.workedHours) : '—'}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-slate-800 dark:text-slate-100">{fmtH(r.best.workedHours)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{fmtH(r.best.paidHours)}</td>
                          <td className={`px-2 py-1.5 text-right tabular-nums ${r.best.premiumPct > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                            {r.best.premiumPct}%
                          </td>
                          <td className="px-2 py-1.5 text-slate-500 dark:text-slate-400">
                            {r.best.blockHours}h &middot; {r.best.crewsUsed} crew &middot; {r.best.blocks.map((b) => b.days).join('/')} days
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <p className="m-0 mt-2 text-[11px] text-slate-400 dark:text-slate-500">
                Hours are counted on each month&rsquo;s real dates, including public holidays &mdash; not a
                4.33-week average. Rosters that tie on hours are separated by
                {plan.rankedOnCost ? ' what they actually cost' : ' paid hours (no pay is captured yet)'};
                no wage is shown here or sent to this screen.
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 dark:border-slate-700">
          <span className="text-[11px] text-slate-500 dark:text-slate-400">
            {acceptedRows.length > 0 && (
              <>
                {acceptedRows.length} month{acceptedRows.length === 1 ? '' : 's'} &middot;{' '}
                <span className="font-mono font-semibold">{fmtH(totals.worked)}</span> worked
                {totals.target > 0 && <> vs <span className="font-mono">{fmtH(totals.target)}</span> target</>}
                {' '}&middot; <span className="font-mono font-semibold">{fmtH(totals.paid)}</span> paid
              </>
            )}
          </span>
          <div className="flex items-center gap-2">
            <button className={btn} onClick={onClose}>Cancel</button>
            <button className={btnPrimary} onClick={apply} disabled={!acceptedRows.length}>
              <Check className="w-3.5 h-3.5" />
              Apply {acceptedRows.length || ''} month{acceptedRows.length === 1 ? '' : 's'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
